import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { verifyPayment } from '../Services/PaymentService';
import { GatewayName, PaymentResponse } from '../Types/PaymentType';
import PaymentSuccessModal from '../Components/PaymentSuccessModal';
import { getCompanyById } from '../../Company/Services/CompanyService';
import { ReceiptCompany } from '../../Shared/Utils/generatePaymentReceiptPDF';

type Outcome = 'VERIFYING' | 'SUCCESS' | 'FAILED';

/**
 * Where a form-post provider drops the customer back.
 *
 * <p>Needed by the two providers that redirect: PayU, which POSTs to a hosted page and comes back
 * with the transaction in the query string, and Cashfree, whose `checkout()` navigates the browser to
 * the gateway and back with `order_id`. Razorpay and Paytm verify in-page instead and never reach
 * this route.
 *
 * <p>It already reads Cashfree's own field names - `order_id` for the order and `cf_payment_id` for
 * the payment - so the provider decision stays here, in one place, rather than being pattern-matched
 * per gateway.
 *
 * <p>Nothing here decides whether the payment succeeded. It forwards the callback to the backend,
 * whose PayU implementation recomputes the response hash and then asks PayU's own API. The one thing
 * this page owns is making sure that call happens exactly once: a reload, a refresh, or React 18's
 * development double-mount would otherwise verify repeatedly and the customer would see the success
 * screen flicker or the receipt download several times over.
 */
const PaymentReturnPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [outcome, setOutcome] = useState<Outcome>('VERIFYING');
  const [message, setMessage] = useState<string>('Confirming your payment with the gateway...');
  const [verifiedPayment, setVerifiedPayment] = useState<PaymentResponse | null>(null);
  const [company, setCompany] = useState<ReceiptCompany | null>(null);

  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const run = async () => {
      const gateway = (searchParams.get('gateway') || '').toUpperCase() as GatewayName;
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });

      if (!gateway) {
        setOutcome('FAILED');
        setMessage('This return link is missing the gateway it came from. Returning to plans...');
        return;
      }

      // Order id under whichever name this provider used. The backend accepts all of these, so this
      // is only about picking one to send as the named field it looks the payment up by.
      const orderId =
        params.txnid || params.orderId || params.razorpay_order_id || params.order_id || '';
      const paymentId =
        params.payuId || params.cf_payment_id || params.paymentId || params.razorpay_payment_id || '';
      const signature = params.hash || params.CHECKSUMHASH || params.razorpay_signature || '';

      if (!orderId || orderId.includes('{')) {
        setOutcome('FAILED');
        // `{` means Cashfree handed back its own unsubstituted `{order_id}` placeholder, which means the
        // return URL was built without it. Caught here so it reports the real cause instead of looking
        // up a payment under a literal placeholder and reporting a generic failure.
        setMessage(
          orderId
            ? 'The gateway returned an unsubstituted order reference, so the return URL is misconfigured. Returning to plans...'
            : 'The gateway did not return an order reference. Returning to plans...'
        );
        return;
      }

      const response = await verifyPayment({
        gateway,
        gatewayOrderId: orderId,
        gatewayPaymentId: paymentId || undefined,
        gatewaySignature: signature || undefined,
        status: params.status || params.resultCode || undefined,
        params,
      });

      if (!response?.success || !response.data || response.data.paymentStatus !== 'SUCCESS') {
        setOutcome('FAILED');
        setMessage(response?.message || 'We could not confirm this payment. Returning to plans...');
        return;
      }

      setVerifiedPayment(response.data);
      setOutcome('SUCCESS');

      // Read live for the receipt's company block rather than snapshotting it onto the payment.
      if (response.data.companyId) {
        const companyResponse = await getCompanyById(response.data.companyId);
        if (companyResponse?.success && companyResponse.data) {
          setCompany(companyResponse.data);
        }
      }
    };

    run().catch(() => {
      setOutcome('FAILED');
      setMessage('Something went wrong while confirming the payment. Returning to plans...');
    });
  }, [searchParams]);

  // Failed returns go back to the plans rather than sitting on a dead screen. Eight seconds is long
  // enough to read why, short enough that nobody waits out a screen with no way forward.
  useEffect(() => {
    if (outcome !== 'FAILED') {
      return;
    }
    const timer = window.setTimeout(() => {
      navigate('/recruiter/subscription-plans');
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [outcome, navigate]);

  const backToPlans = () => navigate('/recruiter/subscription-plans');

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-slate-900 border border-white/10 rounded-3xl p-10 text-center"
      >
        {outcome === 'VERIFYING' && (
          <>
            <ArrowPathIcon className="w-14 h-14 mx-auto text-indigo-400 animate-spin" />
            <h2 className="mt-6 text-xl font-black text-white">Confirming your payment</h2>
            <p className="mt-2 text-sm text-slate-400">{message}</p>
            <p className="mt-6 text-xs text-slate-500">
              Do not close this tab - we are waiting for the gateway to confirm the transaction.
            </p>
          </>
        )}

        {outcome === 'FAILED' && (
          <>
            <XCircleIcon className="w-14 h-14 mx-auto text-rose-400" />
            <h2 className="mt-6 text-xl font-black text-white">Payment not confirmed</h2>
            <p className="mt-2 text-sm text-slate-400">{message}</p>
            <p className="mt-4 text-xs text-slate-500">
              If money was deducted from your account, it is released automatically by the gateway and no
              credits are owed. Contact support with your receipt if this persists.
            </p>
            <button
              onClick={backToPlans}
              className="mt-7 w-full bg-indigo-500 hover:bg-indigo-400 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95"
            >
              Back to plans
            </button>
          </>
        )}
      </motion.div>

      {outcome === 'SUCCESS' && (
        <PaymentSuccessModal
          payment={verifiedPayment}
          gateway={null}
          company={company}
          onClose={backToPlans}
          // Leaves the return URL instead of reloading it. The query string still holds the order id,
          // so reloading would verify again and hand out the receipt again, once every eight seconds,
          // for as long as the tab stayed open.
          onFinished={backToPlans}
        />
      )}
    </div>
  );
};

export default PaymentReturnPage;